import { useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { useToast } from '@/shared/ui/Toast';
import { useResolveWorkItem } from '../../hooks/usePortfolio';
import type { WorkItem } from '../../api/portfolioApi';
import { CATEGORY_LABEL, formatAge, formatDue, isAgencyLink } from './constants';
import styles from './WorkCard.module.css';

const SNOOZE_OPTIONS = [
  { label: '1 day', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '1 week', hours: 168 },
];

interface WorkCardProps {
  item: WorkItem;
  /** Hidden inside a single practice's workspace, where it would be noise. */
  showPractice?: boolean;
  onOpenPractice?: (practiceId: string) => void;
}

/**
 * One piece of work. The practice is metadata on the card rather than the row
 * it lives under — the operator is working a queue, not auditing a roster.
 */
export function WorkCard({ item, showPractice = true, onOpenPractice }: WorkCardProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { startImpersonation } = useImpersonation();
  const resolve = useResolveWorkItem();
  const [snoozeOpen, setSnoozeOpen] = useState(false);

  const due = formatDue(item.hoursUntilDue);
  const isAuto = item.ballWith === 'auto';

  const handlePrimary = () => {
    if (!item.deepLink) return;
    // A deep link into the practice's own app is an impersonation — started
    // explicitly, and audited, rather than implied. One into the agency's own
    // pages (an agent's run log) is not.
    if (!isAgencyLink(item.deepLink))
      startImpersonation({ id: item.practiceId, name: item.practiceName });
    navigate(item.deepLink);
  };

  const handleDismiss = async () => {
    try {
      await resolve.mutateAsync({ id: item.id, update: { status: 'dismissed' } });
      toast.success('Dismissed');
    } catch {
      toast.error('Could not dismiss this item');
    }
  };

  const handleSnooze = async (hours: number) => {
    setSnoozeOpen(false);
    try {
      await resolve.mutateAsync({
        id: item.id,
        update: {
          status: 'snoozed',
          snoozedUntil: new Date(Date.now() + hours * 3_600_000).toISOString(),
        },
      });
      toast.success(`Snoozed for ${hours >= 168 ? 'a week' : hours >= 72 ? '3 days' : 'a day'}`);
    } catch {
      toast.error('Could not snooze this item');
    }
  };

  return (
    <article
      className={clsx(
        styles.card,
        isAuto && styles.auto,
        due?.overdue && styles.overdue,
        !isAuto && !due?.overdue && styles.dueSoon,
      )}
    >
      <div className={styles.body}>
        <div className={styles.meta}>
          {showPractice && (
            <button
              type="button"
              className={styles.practice}
              onClick={() => onOpenPractice?.(item.practiceId)}
            >
              {item.practiceName}
            </button>
          )}
          <span className={styles.category}>{CATEGORY_LABEL[item.category]}</span>
          <span className={styles.code}>{item.code}</span>
          {due ? (
            <span className={clsx(styles.due, due.overdue && styles.dueOverdue)}>{due.text}</span>
          ) : (
            <span className={styles.age}>{formatAge(item.createdAt)} ago</span>
          )}
        </div>

        <h3 className={styles.title}>{item.title}</h3>
        <p className={styles.description}>{item.description}</p>
      </div>

      {!isAuto && (
        <div className={styles.actions}>
          {item.actionLabel && item.deepLink && (
            <button type="button" className={styles.primary} onClick={handlePrimary}>
              {item.actionLabel}
            </button>
          )}
          <div className={styles.secondaryRow}>
            <div className={styles.snoozeWrap}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => setSnoozeOpen((open) => !open)}
                disabled={resolve.isPending}
                aria-expanded={snoozeOpen}
              >
                Snooze
              </button>
              {snoozeOpen && (
                <>
                  <div className={styles.backdrop} onClick={() => setSnoozeOpen(false)} />
                  <div className={styles.snoozeMenu}>
                    {SNOOZE_OPTIONS.map((option) => (
                      <button
                        key={option.hours}
                        type="button"
                        className={styles.snoozeItem}
                        onClick={() => handleSnooze(option.hours)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              className={styles.secondary}
              onClick={handleDismiss}
              disabled={resolve.isPending}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
